import { CommonModule } from '@angular/common';
import { afterNextRender, ChangeDetectorRef, Component, HostListener, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize, Subscription } from 'rxjs';
import { PageNavigation } from '../../shared/page-navigation/page-navigation';
import {
  DailyReconciliationSummary,
  PendingReconciliation,
  ReconciliationCandidate,
  ReconciliationApi,
  ReconciliationType
} from '../../core/reconciliation-api';

@Component({
  selector: 'app-gp-reconciliations',
  imports: [CommonModule, FormsModule, PageNavigation],
  templateUrl: './gp-reconciliations.html'
})
export class GpReconciliations implements OnDestroy {
  type: ReconciliationType = 'PRESENTMENT';
  readonly pageSizes = [10, 25, 50, 100];
  status: 'ALL' | 'DIFFERENCES' | 'COMPLETE' = 'ALL';
  page = 0;
  size = 10;
  dateFrom = this.toInputDate(this.daysAgo(29));
  dateTo = this.toInputDate(new Date());
  rows: DailyReconciliationSummary[] = [];
  pendingDay?: DailyReconciliationSummary;
  pendingLoading = false;
  pendingError = '';
  private summaryRequest?: Subscription;
  private pendingRequest?: Subscription;
  private candidatesRequest?: Subscription;
  private confirmRequest?: Subscription;
  pending: PendingReconciliation[] = [];
  pendingPage = 0;
  pendingTotal = 0;
  pendingTotalPages = 1;
  pendingSize = 10;
  selected?: PendingReconciliation;
  candidates: ReconciliationCandidate[] = [];
  candidatesLoading = false;
  confirmingId?: number;
  comment = '';
  message = '';
  loading = false;
  error = '';

  constructor(private readonly api: ReconciliationApi, private readonly cdr: ChangeDetectorRef) {
    afterNextRender(() => this.load());
  }

  get filteredRows(): DailyReconciliationSummary[] {
    return this.rows.filter(row => this.status === 'ALL'
      || (this.status === 'DIFFERENCES' ? this.canReconcile(row) : !this.canReconcile(row)));
  }

  get total(): number { return this.filteredRows.length; }
  get totalPages(): number { return Math.max(1, Math.ceil(this.total / this.size)); }
  get visibleRows(): DailyReconciliationSummary[] {
    return this.filteredRows.slice(this.page * this.size, (this.page + 1) * this.size);
  }

  changePage(page: number): void {
    if (!this.loading && page >= 0 && page < this.totalPages) this.page = page;
  }

  resetPage(): void { this.page = 0; }

  changePendingSize(): void {
    this.pendingPage = 0;
    this.loadPending();
  }

  selectType(type: ReconciliationType): void {
    if (this.type === type || this.confirmingId !== undefined) return;
    this.type = type;
    this.pendingPage = 0;
    this.closePending();
    this.load();
  }

  load(resetPage = true): void {
    if (this.dateFrom && this.dateTo && this.dateFrom > this.dateTo) {
      this.error = 'La fecha desde no puede ser posterior a la fecha hasta.';
      return;
    }
    if (resetPage) this.page = 0;
    this.summaryRequest?.unsubscribe();
    this.loading = true;
    this.error = '';
    this.message = '';
    this.summaryRequest = this.api.getDailySummary(this.type, this.dateFrom, this.dateTo)
      .pipe(finalize(() => { this.loading = false; this.cdr.markForCheck(); }))
      .subscribe({
        next: rows => {
          this.rows = rows ?? [];
          this.page = Math.min(this.page, this.totalPages - 1);
          this.cdr.markForCheck();
        },
        error: error => {
          this.rows = [];
          this.error = error?.error?.message ?? 'No se pudo cargar el resumen de conciliaciones.';
          this.cdr.markForCheck();
        }
      });
  }

  canReconcile(row: DailyReconciliationSummary): boolean {
    return row.pending > 0 || row.reconciled < row.processed;
  }

  openPending(row: DailyReconciliationSummary): void {
    if (!this.canReconcile(row) || this.confirmingId !== undefined) return;
    this.closePending();
    this.message = '';
    this.pendingDay = { ...row };
    this.pendingPage = 0;
    this.pendingTotal = row.pending;
    this.loadPending();
  }

  closePending(): void {
    if (this.confirmingId !== undefined) return;
    this.pendingRequest?.unsubscribe();
    this.closeAssistant();
    this.pendingDay = undefined;
    this.pendingLoading = false;
    this.pending = [];
    this.pendingError = '';
    this.pendingTotalPages = 1;
  }

  loadPending(): void {
    const day = this.pendingDay;
    if (!day) return;
    this.pendingRequest?.unsubscribe();
    this.pending = [];
    this.pendingLoading = true;
    this.pendingError = '';
    this.pendingRequest = this.api.getPending(day.type, day.date, day.date, this.pendingPage, this.pendingSize)
      .subscribe({
        next: result => {
          this.pendingTotal = result.totalElements ?? 0;
          this.pendingTotalPages = Math.max(1, result.totalPages ?? 1);
          if (this.pendingPage >= this.pendingTotalPages) {
            this.pendingPage = this.pendingTotalPages - 1;
            this.loadPending();
            return;
          }
          this.pendingLoading = false;
          this.pending = result.content ?? [];
          this.cdr.markForCheck();
        },
        error: error => {
          this.pendingLoading = false;
          this.pendingError = error?.error?.message ?? 'No se pudieron cargar los movimientos pendientes.';
          this.cdr.markForCheck();
        }
      });
  }

  openAssistant(row: PendingReconciliation): void {
    if (!this.pendingDay) return;
    this.candidatesRequest?.unsubscribe();
    this.pendingError = '';
    this.selected = row; this.candidates = []; this.comment = ''; this.candidatesLoading = true;
    this.candidatesRequest = this.api.getCandidates(this.pendingDay.type, row.id).pipe(finalize(() => { this.candidatesLoading = false; this.cdr.markForCheck(); }))
      .subscribe({
        next: candidates => { this.candidates = candidates ?? []; this.cdr.markForCheck(); },
        error: error => { this.pendingError = error?.error?.message ?? 'No se pudieron buscar candidatos.'; this.cdr.markForCheck(); }
      });
  }

  confirm(candidate: ReconciliationCandidate): void {
    if (!this.selected || !this.pendingDay || this.confirmingId !== undefined) return;
    this.confirmingId = candidate.id; this.pendingError = '';
    this.confirmRequest = this.api.confirmManual(this.pendingDay.type, this.selected.id, candidate.id, this.comment)
      .pipe(finalize(() => { this.confirmingId = undefined; this.cdr.markForCheck(); }))
      .subscribe({
        next: () => { this.confirmingId = undefined; this.closeAssistant(); this.load(false); this.loadPending(); this.message = 'Movimiento conciliado manualmente.'; },
        error: error => { this.pendingError = error?.error?.message ?? 'No se pudo confirmar la conciliación.'; this.cdr.markForCheck(); }
      });
  }

  closeAssistant(): void {
    if (this.confirmingId !== undefined) return;
    this.candidatesRequest?.unsubscribe();
    this.selected = undefined;
    this.candidates = [];
    this.comment = '';
    this.pendingError = '';
  }

  @HostListener('document:keydown.escape')
  closeDialog(): void {
    if (this.selected) this.closeAssistant();
    else if (this.pendingDay) this.closePending();
  }

  ngOnDestroy(): void {
    this.summaryRequest?.unsubscribe();
    this.pendingRequest?.unsubscribe();
    this.candidatesRequest?.unsubscribe();
    this.confirmRequest?.unsubscribe();
  }
  previousPending(): void { if (this.pendingPage > 0) { this.pendingPage--; this.loadPending(); } }
  nextPending(): void { if (this.pendingPage + 1 < this.pendingTotalPages) { this.pendingPage++; this.loadPending(); } }

  money(value?: number): string {
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(Number(value ?? 0) / 100);
  }

  confidence(score: number): string { return score >= 90 ? 'Alta' : score >= 70 ? 'Probable' : 'Débil'; }

  percentage(row: DailyReconciliationSummary): number {
    return row.processed ? Math.round(row.reconciled * 10000 / row.processed) / 100 : 0;
  }

  private daysAgo(days: number): Date {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date;
  }

  private toInputDate(date: Date): string {
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 10);
  }
}
